import { Grid } from '@material-ui/core';
import {
  Button,
  CurrencyField,
  DateField,
  NewFileUpload,
  NewFileUploadArea,
  TextField,
} from '@roc/ui';
import { Document as DocumentType } from '@roc/feature-types';
import { LineItemUpload } from '@roc/feature-loan-details';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { useLoanStore } from '@roc/feature-loans';
import { isValid } from 'date-fns/esm';
import { format } from 'date-fns';

export type SettlementDetailsFormData = {
  settlementDate: Date | null;
  settlementAmount: number | string;
  uploads: NewFileUpload[];
  loanId: string;
};

export type SettlementDetailsFormProps = {
  handleClose: () => void;
  /** When provided, the upload section uses LineItemUpload (with Upload Area and expand) instead of NewFileUploadArea. */
  document?: DocumentType;
  /** Used together with document when rendering LineItemUpload. */
  expand?: boolean;
  loanId: string;
};

export const SettlementDetailsForm = observer(
  ({ handleClose, document, expand = true, loanId }: SettlementDetailsFormProps) => {
    const { loanStore, loanStore: { loanDetails } } = useLoanStore();
    const [settlementDate, setSettlementDate] = useState(loanDetails.settlementDate || null);
    const [settlementAmount, setSettlementAmount] = useState<number | string>(loanDetails.totalSettlementAmount || '');
    const [uploads, setUploads] = useState<NewFileUpload[]>([]);


    const manageDate = newDate => {
      const newFormattedDate = isValid(newDate) ? format(newDate, 'MM/dd/yyyy') : null;
      setSettlementDate(newFormattedDate);
    };

    const handleSave = async () => {
      loanStore.updateSettlementDetails({
        loanId: Number(loanId),
        expectedPayoffDate: settlementDate,
        totalInterestReceived: settlementAmount,
      });
      handleClose();
    };

    const useLineItemUpload = document != null;

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <DateField
            label="Settlement Date"
            value={settlementDate}
            onChange={newDate => manageDate(newDate)}
            format="MM/dd/yyyy"
            fullWidth
            inputVariant="outlined"
            testId="settlement-details-date"
          />
        </Grid>
        <Grid item xs={12}>
          <CurrencyField
            label="Settlement Amount"
            name="settlementAmount"
            value={settlementAmount}
            onChange={(_, value) => setSettlementAmount(value)}
            fullWidth
            variant="outlined"
            testId="settlement-details-amount"
          />
        </Grid>
        <Grid item xs={12}>
          {useLineItemUpload ? (
            <LineItemUpload document={document} expand={expand} />
          ) : (
            <NewFileUploadArea
              items={uploads}
              setItems={setUploads}
              multiple={false}
            />
          )}
        </Grid>
        <Grid container spacing={2} justifyContent="center">
          <Grid item>
            <Button
              type="button"
              variant="contained"
              color="default"
              testId="settlement-details-cancel"
              onClick={handleClose}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              testId="settlement-details-save"
              onClick={handleSave}
            >
              Save
            </Button>
          </Grid>
        </Grid>
      </Grid>
    );
  }
);
